import { useLayoutEffect, useRef, useState, type ComponentType, type SVGProps } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconBold, IconCode, IconCodeSquare, IconItalic, IconQuote, IconSpoiler, IconStrike, IconUnderline } from "../../components/icons";
import { cn } from "../../lib/cn";
import { comboFromEvent, findAction, GLOBAL_WHILE_TYPING, isMac } from "../settings/keybinds";
import type { Preferences } from "@atrium/shared";
import type { FormatKind } from "./format";

const mod = isMac ? "⌘" : "Ctrl+";
const shift = isMac ? "⇧" : "Shift+";

export const FORMAT_BUTTONS: { kind: FormatKind; label: string; icon: ComponentType<SVGProps<SVGSVGElement>>; shortcut?: string }[] = [
  { kind: "bold", label: "Bold", icon: IconBold, shortcut: `${mod}B` },
  { kind: "italic", label: "Italic", icon: IconItalic, shortcut: `${mod}I` },
  { kind: "underline", label: "Underline", icon: IconUnderline, shortcut: `${mod}U` },
  { kind: "strike", label: "Strikethrough", icon: IconStrike, shortcut: `${mod}${shift}X` },
  { kind: "code", label: "Code", icon: IconCode, shortcut: `${mod}E` },
  { kind: "codeblock", label: "Code block", icon: IconCodeSquare },
  { kind: "spoiler", label: "Spoiler", icon: IconSpoiler },
  { kind: "quote", label: "Quote", icon: IconQuote },
];

/** Composer shortcut for a key event, unless the combo belongs to a shortcut that works while typing. */
export function formatShortcut(e: KeyboardEvent, keybinds: Preferences["keybinds"]): FormatKind | "preview" | null {
  if (!(isMac ? e.metaKey : e.ctrlKey)) return null;
  const combo = comboFromEvent(e);
  if (combo) {
    const action = findAction(keybinds, combo);
    if (action && GLOBAL_WHILE_TYPING.includes(action)) return null;
  }
  // Physical key, so Alt/Option variants (e.g. ⌥P = π) still match.
  const k = /^Key[A-Z]$/.test(e.code) ? e.code.slice(3).toLowerCase() : e.key.toLowerCase();
  if (e.altKey) return !e.shiftKey && k === "p" ? "preview" : null;
  if (e.shiftKey) return k === "x" ? "strike" : null;
  return k === "b" ? "bold" : k === "i" ? "italic" : k === "u" ? "underline" : k === "e" ? "code" : null;
}

export const PREVIEW_SHORTCUT = isMac ? "⌘⌥P" : "Ctrl+Alt+P";

/** Viewport position of a caret offset inside a textarea (mirror-element technique). */
function caretPoint(ta: HTMLTextAreaElement, pos: number) {
  const cs = getComputedStyle(ta);
  const div = document.createElement("div");
  for (const p of [
    "boxSizing",
    "width",
    "borderTopWidth",
    "borderRightWidth",
    "borderBottomWidth",
    "borderLeftWidth",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "fontStyle",
    "fontVariant",
    "fontWeight",
    "fontStretch",
    "fontSize",
    "lineHeight",
    "fontFamily",
    "textAlign",
    "textTransform",
    "textIndent",
    "letterSpacing",
    "wordSpacing",
    "tabSize",
  ] as const)
    div.style[p] = cs[p];
  Object.assign(div.style, { position: "absolute", visibility: "hidden", top: "0", left: "-9999px", whiteSpace: "pre-wrap", overflowWrap: "break-word", wordBreak: "break-word", overflow: "hidden" });
  div.textContent = ta.value.slice(0, pos);
  const span = document.createElement("span");
  span.textContent = ta.value.slice(pos) || ".";
  div.appendChild(span);
  document.body.appendChild(div);
  const rect = ta.getBoundingClientRect();
  const out = { x: rect.left + span.offsetLeft - ta.scrollLeft, y: rect.top + span.offsetTop - ta.scrollTop };
  div.remove();
  return { ...out, top: rect.top };
}

/**
 * Small floating toolbar above the selected text in the composer. Buttons
 * don't take focus, so the selection stays put while formatting.
 */
export function FormatToolbar({
  textarea,
  selection,
  active,
  onFormat,
}: {
  textarea: HTMLTextAreaElement;
  selection: { start: number; end: number };
  active: Set<FormatKind>;
  onFormat: (kind: FormatKind) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const a = caretPoint(textarea, selection.start);
      const b = caretPoint(textarea, selection.end);
      // Centre over the selection when it's on one line, otherwise start at its first line.
      const sameLine = Math.abs(a.y - b.y) < 2;
      const x = sameLine ? (a.x + b.x) / 2 : a.x;
      const y = Math.max(a.y, a.top) - 6;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      setPos({ left: Math.min(Math.max(8, x - w / 2), window.innerWidth - w - 8), top: Math.max(8, y - h) });
    };
    place();
    textarea.addEventListener("scroll", place);
    window.addEventListener("resize", place);
    return () => {
      textarea.removeEventListener("scroll", place);
      window.removeEventListener("resize", place);
    };
  }, [textarea, selection.start, selection.end]);

  return createPortal(
    <div
      ref={ref}
      role="toolbar"
      aria-label="Formatting"
      onMouseDown={(e) => e.preventDefault()}
      style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
      className="fixed z-[60] flex items-center gap-0.5 rounded-lg bg-surface-2 p-0.5 shadow-pop animate-fade-in"
    >
      {FORMAT_BUTTONS.map((b, i) => (
        <span key={b.kind} className="contents">
          {(i === 5 || i === 7) && <span className="mx-0.5 h-5 w-px bg-line" aria-hidden />}
          <Tooltip label={b.label} shortcut={b.shortcut}>
            <button
              type="button"
              tabIndex={-1}
              aria-label={b.label}
              aria-pressed={active.has(b.kind)}
              onClick={() => onFormat(b.kind)}
              className={cn("flex size-8 items-center justify-center rounded-md text-fg-2 hover:bg-surface-3 hover:text-fg", active.has(b.kind) && "bg-surface-3 text-fg")}
            >
              <b.icon className="icon size-[18px]" aria-hidden />
            </button>
          </Tooltip>
        </span>
      ))}
    </div>,
    document.body,
  );
}
